import React, { createContext, useContext, useState, useCallback, ReactNode, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { API_ENDPOINTS } from '../config/api';
import { Notification } from '../components/Notifications/NotificationManager';
import { notificationSound } from '../utils/notificationSound';

// =====================================================
// INTERFACES
// =====================================================

export interface ChatMessage {
  id: string;
  errand_id: string;
  sender_id: string;
  sender_name: string;
  sender_role: 'user' | 'driver';
  message: string;
  message_type: 'text' | 'image' | 'location' | 'status_update' | 'file';
  created_at: string;
  is_read: boolean;
  file_url?: string;
  file_name?: string;
  file_type?: string;
  file_size?: number;
  metadata?: {
    image_url?: string;
    location?: {
      latitude: number;
      longitude: number;
      address: string;
    };
    status?: string;
  };
}

export interface ChatRoom {
  errand_id: string;
  errand_title: string;
  user_id: string;
  driver_id?: string;
  last_message?: ChatMessage;
  unread_count: number;
  created_at: string;
  updated_at: string;
}

export interface SendMessageData {
  errandId: string;
  message: string;
  messageType?: 'text' | 'image' | 'location' | 'status_update' | 'file';
  fileUrl?: string;
  fileName?: string;
  fileType?: string;
  fileSize?: number;
  metadata?: {
    imageUrl?: string;
    location?: {
      latitude: number;
      longitude: number;
      address: string;
    };
    status?: string;
  };
}

// =====================================================
// CONTEXT INTERFACE
// =====================================================

interface ChatContextType {
  // State
  messages: ChatMessage[];
  chatRooms: ChatRoom[];
  activeChatRoom: ChatRoom | null;
  loading: boolean;
  error: string | null;
  notifications: Notification[];

  // Actions
  sendMessage: (data: SendMessageData) => Promise<boolean>;
  fetchMessages: (errandId: string) => Promise<void>;
  fetchChatRooms: () => Promise<void>;
  setActiveChatRoom: (errandId: string) => void;
  markMessagesAsRead: (errandId: string) => Promise<void>;
  clearError: () => void;
  addNotification: (notification: Omit<Notification, 'id' | 'timestamp'>) => void;
  removeNotification: (id: string) => void;
  clearNotifications: () => void;
}

// =====================================================
// CONTEXT CREATION
// =====================================================

const ChatContext = createContext<ChatContextType | undefined>(undefined);

// =====================================================
// PROVIDER COMPONENT
// =====================================================

export const ChatProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [chatRooms, setChatRooms] = useState<ChatRoom[]>([]);
  const [activeChatRoom, setActiveChatRoom] = useState<ChatRoom | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notifications, setNotifications] = useState<Notification[]>([]);

  // Send a new message
  const sendMessage = useCallback(async (data: SendMessageData): Promise<boolean> => {
    try {
      setLoading(true);
      setError(null);

      // Get JWT token from localStorage since we're using JWT auth
      const token = localStorage.getItem('token');
      if (!token) {
        console.log('No JWT token found - user not authenticated yet');
        return false;
      }

      // Get user profile from the token
      const response = await fetch(`${API_ENDPOINTS.BASE}/api/auth/profile`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to get user profile');
      }

      const profileData = await response.json();
      const userId = profileData.profile.id;
      const userRole = profileData.profile.role;
      const userName = profileData.profile.full_name || 'Unknown User';

      // Create message using JWT token
      const { data: message, error } = await supabase
        .from('errand_chat_messages')
        .insert({
          errand_id: data.errandId,
          sender_id: userId,
          sender_name: userName,
          sender_role: userRole === 'courier' ? 'driver' : 'user',
          message: data.message,
          message_type: data.messageType || 'text',
          file_url: data.fileUrl || null,
          file_name: data.fileName || null,
          file_type: data.fileType || null,
          file_size: data.fileSize || null,
          metadata: data.metadata || {},
          is_read: false
        })
        .select()
        .single();

      if (error) {
        console.error('Supabase error:', error);
        throw new Error(`Database error: ${error.message}`);
      }

      // Add message to local state immediately for instant UI update
      setMessages(prev => {
        // Check if message already exists to avoid duplicates
        if (prev.some(m => m.id === message.id)) {
          return prev;
        }
        return [...prev, message];
      });

      // Update chat room last message
      await supabase
        .from('errands')
        .update({ updated_at: new Date().toISOString() })
        .eq('id', data.errandId);

      // Update chat rooms to reflect new message
      setChatRooms(prev => prev.map(room => {
        if (room.errand_id === data.errandId) {
          return {
            ...room,
            last_message: {
              id: message.id,
              errand_id: message.errand_id,
              sender_id: message.sender_id,
              sender_name: message.sender_name,
              sender_role: message.sender_role,
              message: message.message,
              message_type: message.message_type,
              created_at: message.created_at,
              is_read: message.is_read,
              metadata: message.metadata
            },
            updated_at: new Date().toISOString()
          };
        }
        return room;
      }));

      setLoading(false);
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send message');
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch messages for a specific errand
  const fetchMessages = useCallback(async (errandId: string) => {
    try {
      setLoading(true);
      setError(null);

      const { data, error } = await supabase
        .from('errand_chat_messages')
        .select('*')
        .eq('errand_id', errandId)
        .order('created_at', { ascending: true });

      if (error) throw error;
      setMessages(data || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch messages');
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch user's chat rooms
  const fetchChatRooms = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Get JWT token from localStorage since we're using JWT auth
      const token = localStorage.getItem('token');
      if (!token) {
        console.log('No JWT token found - user not authenticated yet');
        return;
      }

      // Get user profile from the token
      const response = await fetch(`${API_ENDPOINTS.BASE}/api/auth/profile`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to get user profile');
      }

      const profileData = await response.json();
      const userId = profileData.profile.id;

      // Get user's errands with chat activity
      const { data, error } = await supabase
        .from('errands')
        .select(`
          id,
          title,
          status,
          user_id,
          driver_id,
          created_at,
          updated_at,
          errand_chat_messages(
            id,
            message,
            created_at,
            sender_id,
            sender_name,
            sender_role,
            is_read
          )
        `)
        .or(`user_id.eq.${userId},driver_id.eq.${userId}`)
        .order('updated_at', { ascending: false });

      if (error) throw error;

      // Transform data into chat rooms
      const rooms: ChatRoom[] = (data || []).map(errand => {
        // Sort messages by created_at to get the latest one
        const sortedMessages = errand.errand_chat_messages?.sort((a: any, b: any) =>
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        ) || [];

        const lastMessage = sortedMessages[0];
        const unreadCount = errand.errand_chat_messages?.filter(
          (msg: any) => !msg.is_read && msg.sender_id !== userId
        ).length || 0;

        return {
          errand_id: errand.id,
          errand_title: errand.title,
          user_id: errand.user_id,
          driver_id: errand.driver_id,
          last_message: lastMessage ? {
            id: lastMessage.id,
            errand_id: errand.id,
            sender_id: lastMessage.sender_id,
            sender_name: lastMessage.sender_name || 'Unknown User',
            sender_role: lastMessage.sender_role || 'user',
            message: lastMessage.message,
            message_type: 'text',
            created_at: lastMessage.created_at,
            is_read: lastMessage.is_read
          } : undefined,
          unread_count: unreadCount,
          created_at: errand.created_at,
          updated_at: errand.updated_at
        };
      });

      setChatRooms(rooms);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch chat rooms');
    } finally {
      setLoading(false);
    }
  }, []);

  // Set active chat room
  const setActiveChatRoomHandler = useCallback((errandId: string) => {
    const room = chatRooms.find(r => r.errand_id === errandId);
    setActiveChatRoom(room || null);
    if (room) {
      fetchMessages(errandId);
    }
  }, [chatRooms, fetchMessages]);

  // Mark messages as read
  const markMessagesAsRead = useCallback(async (errandId: string) => {
    try {
      // Get JWT token from localStorage since we're using JWT auth
      const token = localStorage.getItem('token');
      if (!token) {
        console.log('No JWT token found - user not authenticated yet');
        return;
      }

      // Get user profile from the token
      const response = await fetch(`${API_ENDPOINTS.BASE}/api/auth/profile`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to get user profile');
      }

      const profileData = await response.json();
      const userId = profileData.profile.id;

      await supabase
        .from('errand_chat_messages')
        .update({ is_read: true })
        .eq('errand_id', errandId)
        .neq('sender_id', userId);

      // Update local state
      setMessages(prev => prev.map(msg =>
        msg.errand_id === errandId && msg.sender_id !== userId
          ? { ...msg, is_read: true }
          : msg
      ));

      // Update chat rooms unread count
      setChatRooms(prev => prev.map(room =>
        room.errand_id === errandId
          ? { ...room, unread_count: 0 }
          : room
      ));
    } catch (err) {
      console.error('Failed to mark messages as read:', err);
    }
  }, []);

  // Clear error
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Notification functions
  const addNotification = useCallback((notification: Omit<Notification, 'id' | 'timestamp'>) => {
    const newNotification: Notification = {
      ...notification,
      id: Math.random().toString(36).substr(2, 9),
      timestamp: new Date()
    };
    setNotifications(prev => [...prev, newNotification]);

    // Play notification sound
    notificationSound.play();
  }, []);

  const removeNotification = useCallback((id: string) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  }, []);

  const clearNotifications = useCallback(() => {
    setNotifications([]);
  }, []);

    // Set up real-time subscription for new messages
  useEffect(() => {
    const channel = supabase
      .channel('errand_chat_messages')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'errand_chat_messages'
        },
        (payload) => {
          const newMessage = payload.new as ChatMessage;

          // Get current user ID to check if message is from someone else
          const token = localStorage.getItem('token');
          let currentUserId = '';
          if (token) {
            try {
              const payload = JSON.parse(atob(token.split('.')[1]));
              currentUserId = payload.id;
            } catch (error) {
              console.error('Error decoding JWT token:', error);
            }
          }

          // Add to messages if it's for the active chat room
          if (activeChatRoom && newMessage.errand_id === activeChatRoom.errand_id) {
            setMessages(prev => [...prev, newMessage]);
          }

          // Update chat rooms with proper unread count logic
          setChatRooms(prev => prev.map(room => {
            if (room.errand_id === newMessage.errand_id) {
              const isFromOtherUser = newMessage.sender_id !== currentUserId;
              const isNotInActiveChat = !activeChatRoom || activeChatRoom.errand_id !== newMessage.errand_id;
              const shouldIncrementUnread = isFromOtherUser && isNotInActiveChat && !newMessage.is_read;

              return {
                ...room,
                last_message: {
                  id: newMessage.id,
                  errand_id: newMessage.errand_id,
                  sender_id: newMessage.sender_id,
                  sender_name: newMessage.sender_name,
                  sender_role: newMessage.sender_role,
                  message: newMessage.message,
                  message_type: newMessage.message_type,
                  created_at: newMessage.created_at,
                  is_read: newMessage.is_read,
                  metadata: newMessage.metadata
                },
                // Increment unread count only if message is from other user, not in active chat, and not read
                // Decrement if message is read and was previously unread
                unread_count: shouldIncrementUnread
                  ? room.unread_count + 1
                  : (!isFromOtherUser || newMessage.is_read) && room.unread_count > 0
                    ? Math.max(0, room.unread_count - 1)
                    : room.unread_count,
                updated_at: new Date().toISOString()
              };
            }
            return room;
          }));

          // Show notification if message is from someone else and not in active chat
          if (newMessage.sender_id !== currentUserId &&
              (!activeChatRoom || activeChatRoom.errand_id !== newMessage.errand_id)) {
            // Find the errand title from chat rooms
            const errandRoom = chatRooms.find(room => room.errand_id === newMessage.errand_id);
            const errandTitle = errandRoom?.errand_title || `Errand #${newMessage.errand_id.slice(0, 8)}`;

            addNotification({
              message: newMessage.message,
              sender: newMessage.sender_name,
              errandTitle: errandTitle,
              errandId: newMessage.errand_id
            });
          }
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'errand_chat_messages'
        },
        (payload) => {
          const updatedMessage = payload.new as ChatMessage;

          // Get current user ID
          const token = localStorage.getItem('token');
          let currentUserId = '';
          if (token) {
            try {
              const payload = JSON.parse(atob(token.split('.')[1]));
              currentUserId = payload.id;
            } catch (error) {
              console.error('Error decoding JWT token:', error);
            }
          }

          // If message was marked as read and it's from another user, update unread count
          if (updatedMessage.is_read && updatedMessage.sender_id !== currentUserId) {
            setChatRooms(prev => prev.map(room => {
              if (room.errand_id === updatedMessage.errand_id) {
                // Recalculate unread count for this room
                // We need to fetch the actual count, but for now, decrement if count > 0
                const newUnreadCount = Math.max(0, room.unread_count - 1);
                return {
                  ...room,
                  unread_count: newUnreadCount
                };
              }
              return room;
            }));

            // Update message in local state if it's in the active chat
            if (activeChatRoom && activeChatRoom.errand_id === updatedMessage.errand_id) {
              setMessages(prev => prev.map(msg =>
                msg.id === updatedMessage.id ? { ...msg, is_read: true } : msg
              ));
            }
          }
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'errand_chat_messages'
        },
        (payload) => {
          const updatedMessage = payload.new as ChatMessage;

          // Get current user ID
          const token = localStorage.getItem('token');
          let currentUserId = '';
          if (token) {
            try {
              const payload = JSON.parse(atob(token.split('.')[1]));
              currentUserId = payload.id;
            } catch (error) {
              console.error('Error decoding JWT token:', error);
            }
          }

          // If message was marked as read and it's from another user, update unread count
          if (updatedMessage.is_read && updatedMessage.sender_id !== currentUserId) {
            setChatRooms(prev => prev.map(room => {
              if (room.errand_id === updatedMessage.errand_id) {
                // Recalculate unread count - decrement if count > 0
                const newUnreadCount = Math.max(0, room.unread_count - 1);
                return {
                  ...room,
                  unread_count: newUnreadCount
                };
              }
              return room;
            }));

            // Update message in local state if it's in the active chat
            if (activeChatRoom && activeChatRoom.errand_id === updatedMessage.errand_id) {
              setMessages(prev => prev.map(msg =>
                msg.id === updatedMessage.id ? { ...msg, is_read: true } : msg
              ));
            }
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [activeChatRoom, addNotification]);

  const value: ChatContextType = {
    messages,
    chatRooms,
    activeChatRoom,
    loading,
    error,
    notifications,
    sendMessage,
    fetchMessages,
    fetchChatRooms,
    setActiveChatRoom: setActiveChatRoomHandler,
    markMessagesAsRead,
    clearError,
    addNotification,
    removeNotification,
    clearNotifications
  };

  return (
    <ChatContext.Provider value={value}>
      {children}
    </ChatContext.Provider>
  );
};

// =====================================================
// HOOK
// =====================================================

export const useChat = (): ChatContextType => {
  const context = useContext(ChatContext);
  if (context === undefined) {
    throw new Error('useChat must be used within a ChatProvider');
  }
  return context;
};
